/* eslint-disable react/jsx-wrap-multilines */
/* eslint-disable react/jsx-props-no-spreading */
import { ComponentChild, FunctionalComponent, h } from 'preact';
import { ListContainer, ListWrapper } from '../List';
import { Ref, useEffect } from 'preact/hooks';
import { areServicesFull, useCategoryContext } from '../../contexts/CategoryContext';
import { forwardRef, memo } from 'preact/compat';
import ControllerIdItem from '../ControllerIdItem';
import MapServices from '../ServiceHandler/MapServices';
import { VirtualChildContainer } from '../VirtualList/VirtualList.style';
import VirtualList from '../VirtualList';
import VirtualServiceItem from '../VirtualList/VirtualServiceItem';
import { getItemStyle } from '../VirtualList/helpers';
import { useSubService } from '../../hooks/useSubService';
import { useTheme } from 'styled-components';
import { useUiContext } from '../../contexts/UiContext';
import { useVirtualList } from '../../hooks';

const CONTROLLER_ID_ITEM = 1;
const LIST_TOP_SPACE = 1;

type Props = {
  controllerIdLabel: string;
  isVisible: boolean;
  showServicesToggles: boolean;
  title?: string;
  stickyRef: Ref<HTMLDivElement>;
};

const ServicesList: FunctionalComponent<Props> = forwardRef<HTMLDivElement, Props>(
  ({ controllerIdLabel, isVisible, showServicesToggles = true, title, stickyRef }, virtualListRef) => {
    const { categories, onServiceToggle, services } = MapServices(useCategoryContext());
    const servicesCount = services.length + CONTROLLER_ID_ITEM + LIST_TOP_SPACE;
    const itemCount = title == null ? servicesCount : servicesCount + 1;
    const { getItemSize, setItemSize } = useVirtualList(itemCount, 50);
    const { clearServiceVendorScrollToId, scrollToIdTabViewServiceVendors } = useUiContext();
    const { onCloseSubServiceDetail, onOpenSubServiceDetail } = useSubService();
    const theme = useTheme();

    // Clears the scroll to index to avoid jumping back on it after the component rendered
    useEffect(() => {
      if (isVisible) {
        onCloseSubServiceDetail();
        clearServiceVendorScrollToId();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isVisible]);

    const getScrollIndexForId = (): number => {
      if (scrollToIdTabViewServiceVendors && typeof scrollToIdTabViewServiceVendors === 'string') {
        const index = services.findIndex((service) => service.id === scrollToIdTabViewServiceVendors);

        // index found --> return correct whole list index by adding static Placeholder in the top of the list
        if (index >= 0) {
          return index + LIST_TOP_SPACE;
        }
      }

      return -1;
    };

    const renderItem = (index: number): ComponentChild => {
      // always render controllerID in the last position of the list
      if (index === itemCount - 1) {
        return <ControllerIdItem label={controllerIdLabel} />;
      }

      if (areServicesFull(services)) {
        return (
          <VirtualServiceItem
            categories={categories}
            hideToggle={!showServicesToggles}
            hasTopSpace
            index={index}
            id={index}
            onOpenSubServiceDetail={onOpenSubServiceDetail}
            onToggleService={onServiceToggle}
            getItemSize={getItemSize}
            setItemSize={setItemSize}
            services={services}
            title={title}
            {...(scrollToIdTabViewServiceVendors && { scrollToId: scrollToIdTabViewServiceVendors })}
          />
        );
      }

      return null;
    };

    return (
      <ListContainer>
        <ListWrapper>
          <VirtualList
            data-testid="uc-virtual-list"
            ref={virtualListRef}
            estimatedItemSize={50}
            isVisible={isVisible}
            itemCount={itemCount}
            itemSize={getItemSize}
            marginBottom={theme.spacing.base.lg}
            stickyRef={stickyRef}
            {...(scrollToIdTabViewServiceVendors && { scrollToIndex: getScrollIndexForId() })}
            renderItem={({ index, style }): ComponentChild => (
              <VirtualChildContainer
                data-testid={`uc-virtual-list-item-${index}`}
                key={index}
                style={getItemStyle(style)}
              >
                {renderItem(index)}
              </VirtualChildContainer>
            )}
            width="100%"
          />
        </ListWrapper>
      </ListContainer>
    );
  },
);

export default memo(ServicesList);
